/* ============================================================
   AUTH CONTEXT — tracks the signed-in user and whether they are
   an allow-listed admin (an admins/{uid} doc exists). The UI uses
   isAdmin to gate the backoffice; security rules enforce it for real.
   ============================================================ */
import { createContext, useContext, useEffect, useState } from 'react'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '../lib/firestore.js'
import { watchAuth, signIn, signOut, resetPassword, changePassword } from '../lib/auth.js'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [isAdmin, setIsAdmin] = useState(false)
  // True only once the admin allow-list check for the current user has
  // finished. Until then we must not decide "authorised vs refused" — doing
  // so flashes the refusal screen during the (async) check right after login.
  const [checked, setChecked] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    return watchAuth(async (u) => {
      setChecked(false)
      if (u) {
        try {
          // Force a token refresh so a freshly-set admin custom claim
          // (used by Storage rules) is present without a re-login.
          await u.getIdToken(true)
          const snap = await getDoc(doc(db, 'admins', u.uid))
          setIsAdmin(snap.exists())
        } catch {
          setIsAdmin(false)
        }
      } else {
        setIsAdmin(false)
      }
      // Set the user and the "checked" flag together, so consumers never see
      // a signed-in user whose admin status hasn't been resolved yet.
      setUser(u)
      setChecked(true)
      setLoading(false)
    })
  }, [])

  return (
    <AuthContext.Provider
      value={{ user, isAdmin, checked, loading, signIn, signOut, resetPassword, changePassword }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
